import React, { useState, useEffect, Suspense } from 'react'
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { API_BASE, GOOGLE_CLIENT_ID, IOS_CLIENT_ID as IOS_CLIENT_ID_CONFIG } from './config'

import ManagementPortal from './pages/ManagementPortal'
import ResidentPortal from './pages/ResidentPortal'
import ServiceStaffPortal from './pages/ServiceStaffPortal'
import AccessDenied from './pages/AccessDenied'
import CommunityRegistration from './pages/CommunityRegistration'
import ResidentRegister from './pages/ResidentRegister'
import AppAdminPortal from './pages/AppAdminPortal'
import { registerResident } from './services/onboardingService'
import { getPortalRoute, normalizeRole } from './utils/roleRouting'
import { normalizeEmail, validateEmail } from './utils/validation'

function readSessionValue(record, ...keys) {
  if (!record || typeof record !== 'object') return ''

  for (const key of keys) {
    const value = record[key]
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value)
    }
  }

  const lowerKeys = Object.fromEntries(
    Object.entries(record).map(([key, value]) => [String(key).toLowerCase(), value])
  )

  for (const key of keys) {
    const normalizedKey = String(key).toLowerCase()
    const value = lowerKeys[normalizedKey]
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value)
    }
  }

  return ''
}

// Store selected community data into sessionStorage so portals can read it
function storeUserSession(data) {
  try {
    const cid = readSessionValue(data, 'COMMUNITY_ID', 'communityId', 'community_id')
    if (cid) sessionStorage.setItem('community_id', cid)
    const cname = readSessionValue(data, 'COMMUNITY_NAME', 'communityName', 'community_name')
    if (cname) sessionStorage.setItem('community_name', cname)
    const unitNbr = readSessionValue(data, 'UNIT_NBR', 'unitNbr', 'unit_nbr', 'unit')
    if (unitNbr) sessionStorage.setItem('unit_nbr', unitNbr)
    const userId = readSessionValue(data, 'USER_ID', 'userId', 'user_id')
    if (userId) sessionStorage.setItem('user_id', userId)
    else sessionStorage.removeItem('user_id')
    const role = normalizeRole(readSessionValue(data, 'ROLE', 'role', 'user_role'))
    if (role) sessionStorage.setItem('user_role', role)
  } catch (e) { /* sessionStorage unavailable */ }
}

function decodeJwtEmail(token) {
  try {
    if (!token || typeof token !== 'string') return ''
    const parts = token.split('.')
    if (parts.length < 2) return ''
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = JSON.parse(atob(payload))
    return (json.email || '').toString().toLowerCase()
  } catch (e) {
    return ''
  }
}

// Community selection screen — shown when a user belongs to multiple communities
function CommunitySelector() {
  const navigate = useNavigate()
  const location = useLocation()
  const communities = location.state?.communities || []
  const email = location.state?.email || ''
  const firstCommunityId =
    readSessionValue(communities[0], 'COMMUNITY_ID', 'communityId', 'community_id') || ''
  const allSameCommunity = communities.length > 1 && communities.every((comm) => {
    const cid = readSessionValue(comm, 'COMMUNITY_ID', 'communityId', 'community_id')
    return cid && cid === firstCommunityId
  })

  if (!communities.length) {
    navigate('/')
    return null
  }

  function selectCommunity(comm) {
    storeUserSession(comm)
    navigate(getPortalRoute(comm))
  }

  return (
    <div className="page-root">
      <div className="hero">
        <div className="logo-wrap">
          <div className="logo-circle">
            <img src="/apartment-icon.svg" alt="Smart Community" width={40} height={40} />
          </div>
        </div>
        <h1 className="brand">{allSameCommunity ? 'Select Your Apartment' : 'Select Your Community'}</h1>
        <p className="tag">
          {allSameCommunity
            ? 'You are linked to multiple apartments in this community. Choose one to continue.'
            : 'You are registered in multiple communities. Choose one to continue.'}
        </p>

        <div className="card" style={{ marginTop: 24 }}>
          <h2 style={{ marginBottom: 6, color: '#1e3a5f' }}>{allSameCommunity ? 'Your Apartments' : 'Your Communities'}</h2>
          <p className="muted" style={{ marginBottom: 16 }}>{email}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {communities.map((comm) => (
              <button
                key={`${comm.COMMUNITY_ID || comm.communityId || 'comm'}-${comm.USER_ID || comm.userId || 'uid'}-${comm.UNIT_NBR || comm.unitNbr || 'unit'}-${comm.ROLE || comm.role || 'role'}-${comm.USER_TYPE || comm.userType || 'type'}`}
                onClick={() => selectCommunity(comm)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '14px 16px',
                  borderRadius: 12,
                  border: '2px solid #e5e7eb',
                  background: '#f9fafb',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb' }}
              >
                <span style={{ fontSize: 17, fontWeight: 700, color: '#1e3a5f' }}>
                  {comm.COMMUNITY_NAME || comm.COMMUNITY_ID}
                </span>
                <span style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  Role: <strong style={{ color: '#374151' }}>{comm.ROLE}</strong>
                  {comm.USER_TYPE ? ` (${comm.USER_TYPE})` : ''}
                  {comm.UNIT_NBR ? ` · Unit ${comm.UNIT_NBR}` : ''}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="copyright">© 2026 Smart Community Manager. All rights reserved.</div>
      </div>
    </div>
  )
}

function LoginPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isRegistered, setIsRegistered] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  // Load saved email from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('rememberMe_email')
      if (saved) {
        setEmail(saved)
        setRememberMe(true)
      }
    } catch (e) { /* localStorage unavailable */ }
  }, [])

  // Detect if running under Capacitor
  const isCapacitorNative = (() => {
    if (typeof window === 'undefined') return false
    try {
      if (window.Capacitor) {
        if (typeof window.Capacitor.isNativePlatform === 'function') {
          return window.Capacitor.isNativePlatform()
        }
        if (typeof window.Capacitor.getPlatform === 'function') {
          return window.Capacitor.getPlatform() !== 'web'
        }
      }
      if (navigator && typeof navigator.userAgent === 'string' && navigator.userAgent.includes('Capacitor')) return true
      if (window.location && typeof window.location.protocol === 'string' && window.location.protocol === 'capacitor:') return true
    } catch (e) {
      // ignore
    }
    return false
  })()

  // Listen for deep-link from OAuth callback
  useEffect(() => {
    if (!isCapacitorNative) return
    let removeListener = null
    const setup = async () => {
      try {
        const { App } = await import('@capacitor/app')
        const handler = async (event) => {
          const url = event?.url || ''
          console.log('App URL opened:', url)
          if (!url.includes('smartcommui://oauth')) return
          
          try {
            const u = new URL(url)
            const params = new URLSearchParams(u.search)
            const idToken = params.get('idToken')
            const accessToken = params.get('accessToken')
            const email = params.get('email')
            
            console.log('OAuth tokens received via deep-link:', { hasIdToken: !!idToken, hasAccessToken: !!accessToken, email })
            
            if (idToken && email) {
              console.log('Registering user:', email)
              setLoading(true)
              try {
                const url = `${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`
                const resp = await fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } })
                if (resp.ok) {
                  const data = await resp.json()
                  const returned = ((data.emailId || data.email || data.Email || data.EMAIL) || '').toString().toLowerCase()
                  if (returned === email) {
                    try { if (accessToken) sessionStorage.setItem('access_token', accessToken) } catch (e) { }
                    try { sessionStorage.setItem('id_token', idToken) } catch (e) { }
                    setLoading(false)
                    const route = getPortalRoute(data, { fallback: '/management' })
                    console.log('Registration successful, navigating to', route)
                    navigate(route)
                    return
                  }
                }
              } catch (err) {
                console.error('registration check failed', err)
              }
              setLoading(false)
              navigate('/access-denied')
            }
          } catch (err) {
            console.error('error processing deep-link', err)
          }
        }
        const listener = App.addListener('appUrlOpen', handler)
        removeListener = () => { listener.remove() }
      } catch (e) {
        console.warn('App plugin not available', e)
      }
    }
    setup()
    return () => { if (removeListener) removeListener() }
  }, [navigate, isCapacitorNative])

  // Native Google Auth is handled via custom bridge (nativeGoogleAuth.js)
  // No third-party Capacitor plugin needed.

  async function handleGoogleLogin(explicitEmail, explicitToken) {
    setError('')
    const trimmed = normalizeEmail(explicitEmail || email || '')
    console.log('Checking registration for email:', trimmed, 'type:', typeof trimmed)
    if (typeof trimmed !== 'string' || trimmed.length === 0) {
      setError('Please enter your email address')
      return
    }
    if (!validateEmail(trimmed)) {
      setError('Please enter a valid email address')
      return
    }
    const isGmail = trimmed.endsWith('@gmail.com')
    if(!isGmail){
      setError('Please enter a valid Gmail address')
      return
    }

    // Persist or clear email based on Remember Me
    try {
      if (rememberMe) {
        localStorage.setItem('rememberMe_email', trimmed)
      } else {
        localStorage.removeItem('rememberMe_email')
      }
    } catch (e) { /* localStorage unavailable */ }

      // Call the registration check service
      setLoading(true)
      setError('')
      try {
        const url = `${API_BASE}/reg-user?emailId=${encodeURIComponent(trimmed)}`
        const resp = await fetch(url, { method: 'GET' })
        if (resp.ok) {
          const data = await resp.json()

          // Multi-community: let user pick
          if (data.multiCommunity && Array.isArray(data.communities)) {
            setLoading(false)
            navigate('/select-community', { state: { communities: data.communities, email: trimmed } })
            return
          }

          const returned = ((data.emailId || data.email || data.Email || data.EMAIL) || '').toString().toLowerCase()
          const matched = returned === trimmed
          setIsRegistered(matched)
          if (!matched) {
            setLoading(false)
            navigate('/access-denied')
            return
          }
          // success — store session and route based on role
          storeUserSession(data)
          const route = getPortalRoute(data, { fallback: '/management' })
          setTimeout(() => {
            setLoading(false)
            navigate(route)
          }, 300)
        } else {
          // treat non-200 as not registered
          setIsRegistered(false)
          setLoading(false)
          navigate('/access-denied')
        }
        // no token cleanup needed when not persisting tokens
      } catch (err) {
        setLoading(false)
        setError('Registration check failed. Please try again.')
        console.error('registration check error', err)
      }
  }

  // Redirect user to Google's OAuth2 consent screen (implicit id_token flow).
  async function redirectToGoogle(state = '') {
    const CLIENT_ID = GOOGLE_CLIENT_ID
    
    // Use explicit http://localhost for native (Capacitor) so Google accepts it.
    // On web use the real origin.
    const redirectUri = isCapacitorNative ? 'http://localhost/oauth2/callback' : `${window.location.origin}/oauth2/callback`
    
    const nonce = Math.random().toString(36).substring(2)
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'token id_token',
      scope: 'openid email profile',
      nonce,
      prompt: 'select_account',
      ...(state ? { state } : {}),
    })
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
    
    console.log('Redirecting to Google OAuth with redirect_uri:', redirectUri)
    
    if (isCapacitorNative) {
      try {
        const { Browser } = await import('@capacitor/browser')
        await Browser.open({ url: authUrl })
        return
      } catch (e) {
        console.warn('Browser plugin error', e)
      }
    }
    
    // For web, use direct navigation
    window.location.href = authUrl
  }

  // Validate email is Gmail then start OAuth redirect
  function startGoogleSignIn() {
    setError('')
    const trimmed = normalizeEmail(email || '')
    if (trimmed.length === 0) {
      setError('Please enter your email address')
      return
    }
    if (!validateEmail(trimmed)) {
      setError('Please enter a valid email address')
      return
    }
    const isGmail = trimmed.endsWith('@gmail.com')
    if (!isGmail) {
      setError('Only Gmail addresses are supported for sign in')
      return
    }

    // Persist or clear email based on Remember Me
    try {
      if (rememberMe) {
        localStorage.setItem('rememberMe_email', trimmed)
      } else {
        localStorage.removeItem('rememberMe_email')
      }
    } catch (e) { /* localStorage unavailable */ }

    // Try native PKCE plugin first (if available). If it fails, fall back to browser flow.
    ;(async () => {
      console.log('startGoogleSignIn: isCapacitorNative=', isCapacitorNative)
      try {
        const mod = await import('./nativeGoogleAuth')
        if (mod?.nativeSignIn && isCapacitorNative) {
          console.log('Attempting native PKCE sign-in')
          await nativeGoogleSignIn(trimmed)
          return
        }
      } catch (e) {
        console.warn('nativeSignIn import failed or not available', e)
      }
      console.log('Falling back to browser OAuth')
      await redirectToGoogle()
    })()
  }

  // Native sign-in via native bridge (iOS: AppDelegate, Android: MainActivity)
  async function nativeGoogleSignIn(prefilledEmail) {
    console.log('nativeGoogleSignIn: attempting native path', { prefilledEmail })
    setLoading(true)
    setError('')
    try {
      const { nativeSignIn } = await import('./nativeGoogleAuth')

      // Both iOS and Android use the same iOS OAuth client ID because it
      // supports custom-scheme redirects (reversed-client-ID://). Android-type
      // OAuth clients only work with Google Sign-In SDK, NOT browser redirect flows.
      const platform = window.Capacitor?.getPlatform?.() || 'unknown'
      const CLIENT_ID = IOS_CLIENT_ID_CONFIG

      console.log('Calling nativeSignIn with clientId:', CLIENT_ID, 'platform:', platform)
      const res = await nativeSignIn({ clientId: CLIENT_ID })
      console.log('Native auth result keys:', res ? Object.keys(res) : 'null')

      const idToken = res?.id_token || res?.idToken || null
      const accessToken = res?.access_token || res?.accessToken || null
      const refreshToken = res?.refresh_token || null
      const isRegistered = res?._registered === true
      const nativeEmail = (res?._email || '').toString().toLowerCase()
      const decodedEmail = decodeJwtEmail(idToken) || nativeEmail
      const lookupEmail = decodedEmail || nativeEmail

      console.log('nativeGoogleSignIn: idToken?', !!idToken, 'registered?', isRegistered, 'email:', lookupEmail)

      if (!idToken) {
        console.warn('Native auth returned no id_token')
        setError('Authentication succeeded but no identity token received. Please try again.')
        setLoading(false)
        return
      }

      // Store tokens
      try { if (accessToken) sessionStorage.setItem('access_token', accessToken) } catch (e) {}
      try { if (refreshToken) sessionStorage.setItem('refresh_token', refreshToken) } catch (e) {}
      try { sessionStorage.setItem('id_token', idToken) } catch (e) {}

      let nativeRoute = '/management'
      let userData = null
      let resolvedEmail = lookupEmail

      try {
        const regUrl = `${API_BASE}/reg-user?emailId=${encodeURIComponent(resolvedEmail || decodedEmail || '')}`
        const regResp = await fetch(regUrl, { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } })
        if (regResp.ok) {
          userData = await regResp.json()
          console.log('nativeGoogleSignIn: reg-user data:', JSON.stringify(userData).substring(0, 300))
          const returned = ((userData.emailId || userData.email || userData.Email || userData.EMAIL) || '').toString().toLowerCase()
          resolvedEmail = returned || resolvedEmail
          if (returned && returned !== decodedEmail && returned !== nativeEmail) {
            console.warn('nativeGoogleSignIn: reg-user returned an unexpected email address, continuing with fallback route')
          }
          // Multi-community: show the community selector
          if (userData.multiCommunity && Array.isArray(userData.communities)) {
            setLoading(false)
            navigate('/select-community', { state: { communities: userData.communities, email: resolvedEmail || lookupEmail } })
            return
          }
          storeUserSession(userData)
          nativeRoute = getPortalRoute(userData, { fallback: '/management' })
        } else {
          console.warn('nativeGoogleSignIn: reg-user lookup failed with status', regResp.status)
        }
      } catch (regErr) {
        console.warn('nativeGoogleSignIn: failed to fetch user role, falling back to default route', regErr)
      }

      setLoading(false)
      if (userData || isRegistered || lookupEmail) {
        console.log('nativeGoogleSignIn: navigating to', nativeRoute)
        navigate(nativeRoute)
      } else {
        console.log('nativeGoogleSignIn: user could not be resolved from the native response, navigating to /access-denied')
        navigate('/access-denied')
      }
    } catch (err) {
      console.error('native auth flow failed:', err?.message || err)
      setError('Sign-in failed: ' + (err?.message || 'Unknown error. Please try again.'))
      setLoading(false)
    }
  }

  return (
    <div className="page-root">
      <div className="hero">
        <div className="logo-wrap">
          <div className="logo-circle" style={{ background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Apartment Image Icon */}
            <img src="/apartment-icon.svg" alt="Apartment Icon" width={48} height={48} style={{ display: 'block' }} />
          </div>
        </div>

        <h1 className="brand">Smart Community Manager</h1>
        <p className="tag">All-in-One Property Management</p>

        <div className="card">
          <h2>Sign in to continue</h2>
          <p className="muted">Use your Google account to sign in</p>

          <label className="block mb-2 text-sm">Email address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputMode="email"
            placeholder="you@company.com"
            className="w-full p-2 border rounded mb-3"
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '14px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
            />
            Remember me
          </label>
          {error && <div className="text-red-600 text-sm mb-2">{error}</div>}

          <div className="buttons">
            <button className="btn primary" onClick={startGoogleSignIn} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in with Google'}
            </button>
          </div>

          <p style={{ textAlign: 'center', marginTop: 16, marginBottom: 0, fontSize: 17, color: '#374151', fontWeight: 500 }}>
            New community?{' '}
            <span
              style={{ color: '#2563eb', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 18 }}
              onClick={() => navigate('/community-register')}
            >Register your Community</span>
          </p>

          <div className="stats">
            <div className="stat"><div className="num">7</div><div className="label">Features</div></div>
            <div className="stat"><div className="num">24/7</div><div className="label">Access</div></div>
            <div className="stat"><div className="num">100%</div><div className="label">Secure</div></div>
          </div>
        </div>

        <div className="copyright">© 2026 Smart Community Manager. All rights reserved.</div>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/scm/login" element={<LoginPage />} />
        <Route path="/oauth2/callback" element={<OAuthCallback />} />
        <Route path="/select-community" element={<CommunitySelector />} />
        <Route path="/management" element={<ManagementPortal />} />
        <Route path="/resident" element={<ResidentPortal />} />
        <Route path="/service-staff" element={<ServiceStaffPortal />} />
        <Route path="/app-admin" element={<AppAdminPortal />} />
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="/community-register" element={<CommunityRegistration />} />
        <Route path="/resident-register" element={<ResidentRegister />} />
      </Routes>
    </BrowserRouter>
  )
}

function OAuthCallback() {
  // This route receives the id_token in the URL fragment (#id_token=...)
  // On native, we'll manually trigger a deep-link to get back to the app
  // On web, we'll navigate to /management
  const navigate = useNavigate()

  useEffect(() => {
    (async () => {
      const hash = window.location.hash.replace(/^#/, '')
      const params = new URLSearchParams(hash)
      const idToken = params.get('id_token')
      const accessToken = params.get('access_token')

      if (!idToken) {
        // If the hash is empty we're not actually at the OAuth callback URL —
        // this happens in React StrictMode (effects run twice) after the first
        // run already navigated away. Do nothing.
        if (!hash || hash.length === 0) return
        console.error('no id_token in callback, hash was:', hash.substring(0, 100))
        navigate('/access-denied')
        return
      }

      // Check for community registration flow (state param is reliable — returned by Google in hash)
      const oauthState = params.get('state')
      // Also check localStorage intent flag (most reliable across full-page redirects)
      let localIntent = ''
      try { localIntent = localStorage.getItem('oauth_intent') || '' } catch (e) {}
      const isCommunityReg = oauthState === 'community_registration' || localIntent === 'community_registration'
      if (isCommunityReg) {
        try { localStorage.removeItem('oauth_intent') } catch (e) {}
        try { sessionStorage.setItem('id_token', idToken) } catch (e) {}
        if (accessToken) { try { sessionStorage.setItem('access_token', accessToken) } catch (e) {} }
        // Pass the token directly via navigation state so CommunityRegistration doesn't need flags
        navigate('/community-register', { state: { idToken, fromOAuth: true } })
        return
      }

      // Check for pending community registration via sessionStorage flag (legacy fallback)
      const pendingCommunityReg = sessionStorage.getItem('pending_community_registration')
      if (pendingCommunityReg) {
        sessionStorage.removeItem('pending_community_registration')
        try { sessionStorage.setItem('id_token', idToken) } catch (e) {}
        if (accessToken) { try { sessionStorage.setItem('access_token', accessToken) } catch (e) {} }
        navigate('/community-register', { state: { idToken, fromOAuth: true } })
        return
      }

      // Check for pending resident registration (from ResidentRegister page)
      const pendingRaw = sessionStorage.getItem('pending_resident_registration')

      if (pendingRaw) {
        try {
          const pending = JSON.parse(pendingRaw)
          const parts = idToken.split('.')
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
          const googleEmail = (payload.email || '').toLowerCase()
          sessionStorage.removeItem('pending_resident_registration')
          if (googleEmail !== (pending.email || '').toLowerCase()) {
            console.error('Resident registration: email mismatch', googleEmail, pending.email)
            navigate('/access-denied')
            return
          }
          try { sessionStorage.setItem('id_token', idToken) } catch (e) {}
          await registerResident({
            token: pending.token,
            email: googleEmail,
            firstName: pending.firstName,
            lastName: pending.lastName,
          })
          navigate('/resident')
          return
        } catch (err) {
          console.error('Resident registration via OAuthCallback failed:', err)
          navigate('/access-denied')
          return
        }
      }

      // Decode JWT payload
      let email = ''
      try {
        const parts = idToken.split('.')
        if (parts.length !== 3) {
          throw new Error('Invalid JWT format')
        }
        
        // Decode the payload (second part)
        const payload = parts[1]
          .replace(/-/g, '+')
          .replace(/_/g, '/')
        
        const decoded = JSON.parse(atob(payload))
        email = (decoded.email || '').toString().toLowerCase()
        console.log('Successfully decoded email from token:', email)
      } catch (err) {
        console.error('Failed to parse id_token:', err.message)
        navigate('/access-denied')
        return
      }

      if (!email) {
        console.error('No email found in token')
        navigate('/access-denied')
        return
      }

      // Call registration API directly with Authorization header
      try {
        const url = `${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`
        console.log('Calling registration API:', url)
        const resp = await fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${idToken}` } })
        
        if (!resp.ok) {
          console.error('API returned error:', resp.status, resp.statusText)
          navigate('/access-denied')
          return
        }
        
        const data = await resp.json()

        // Multi-community — navigate to selector screen
        if (data.multiCommunity && Array.isArray(data.communities)) {
          try {
            if (idToken) sessionStorage.setItem('id_token', idToken)
            if (accessToken) sessionStorage.setItem('access_token', accessToken)
          } catch (e) {}
          navigate('/select-community', { state: { communities: data.communities, email } })
          return
        }

        const returned = ((data.emailId || data.email || data.Email || data.EMAIL) || '').toString().toLowerCase()
        console.log('API returned email:', returned, 'expected:', email)
        
        if (returned === email) {
          // Store tokens for later use
          try {
            if (idToken) sessionStorage.setItem('id_token', idToken)
            if (accessToken) sessionStorage.setItem('access_token', accessToken)
          } catch (e) { }
          storeUserSession(data)
          
          // Attempt to deep-link back to the native app first (works when opened in system Safari).
          // We create a hidden iframe to invoke the custom scheme so the callback can remain
          // loaded and perform a fallback to the web flow if the app is not installed.
          try {
            // Check if we're in Capacitor native environment and deep-link back to app
            const isCapacitorNative = typeof window !== 'undefined' && (
              (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform()) ||
              (window.location && typeof window.location.origin === 'string' && window.location.origin.startsWith('capacitor://'))
            )
            
            if (isCapacitorNative) {
              console.log('OAuth successful on native, deep-linking back to app')
              // Deep-link back to app with tokens
              const queryStr = new URLSearchParams({ idToken, accessToken, email }).toString()
              const deeplink = `smartcommui://oauth?${queryStr}`
              console.log('Redirecting to:', deeplink)
              window.location.href = deeplink
              return
            } else {
              // On web, navigate based on role
              const route = getPortalRoute(data, { fallback: '/management' })
              console.log('OAuth successful on web, navigating to', route)
              navigate(route)
              return
            }
          } catch (err) {
            console.warn('Deep-link attempt via window.location failed, falling back to web navigation', err)
            const route = getPortalRoute(data, { fallback: '/management' })
            navigate(route)
            return
          }
          return
        } else {
          console.error('Email mismatch from API')
        }
      } catch (err) {
        console.error('registration API call failed', err.message)
      }

      navigate('/access-denied')
    })()
  }, [navigate])


  return <div className="page-root"><div className="card">Signing you in…</div></div>
}
